import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Hash,
    Stamp,
    Fingerprint,
    KeyRound,
    Clock,
    ShieldCheck,
    ShieldAlert,
    HelpCircle,
    FileText,
    Copy,
    Check,
} from "lucide-react";
import { fetchPublicReceipt } from "@/lib/api";

const PublicReceiptPage = () => {
    const { hash } = useParams();
    const [receipt, setReceipt] = useState(null);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetchPublicReceipt(hash)
            .then((d) => !cancelled && setReceipt(d))
            .catch((e) => {
                if (!cancelled) {
                    const msg =
                        e?.response?.data?.detail?.message ||
                        e?.response?.data?.detail ||
                        "Receipt not found";
                    setError(typeof msg === "string" ? msg : "Receipt not found");
                }
            });
        return () => {
            cancelled = true;
        };
    }, [hash]);

    const onCopy = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            /* ignore */
        }
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <header className="border-b border-slate-200 bg-white">
                <div className="mx-auto max-w-5xl px-6 lg:px-10 py-6 flex items-center justify-between gap-4 flex-wrap">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900"
                        data-testid="public-back-link"
                    >
                        <ArrowLeft className="h-4 w-4" /> Back to PromptCierge
                    </Link>
                    <div className="flex items-center gap-3">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                            Public-safe receipt · /r/:hash
                        </div>
                        <button
                            onClick={onCopy}
                            data-testid="public-copy-link"
                            className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
                        >
                            {copied ? (
                                <>
                                    <Check className="h-3.5 w-3.5 text-emerald-600" /> Copied
                                </>
                            ) : (
                                <>
                                    <Copy className="h-3.5 w-3.5" /> Copy link
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-5xl px-6 lg:px-10 py-12 space-y-8">
                {error && (
                    <ErrorCard message={error} hash={hash} />
                )}
                {!error && !receipt && <LoadingCard />}
                {receipt && <ReceiptDocument receipt={receipt} />}

                {receipt && (
                    <footer
                        data-testid="public-footer"
                        className="border-t border-slate-200 pt-6 text-xs text-slate-500 font-mono"
                    >
                        This public view exposes only allow-listed safe fields.
                        The full signed envelope, cert material and private
                        governance payload are stored server-side and never published.
                    </footer>
                )}
            </main>
        </div>
    );
};

const LoadingCard = () => (
    <div data-testid="public-loading" className="font-mono text-sm text-slate-500">
        Loading public receipt…
    </div>
);

const ErrorCard = ({ message, hash }) => (
    <div
        data-testid="public-receipt-error"
        className="border border-amber-200 bg-amber-50 rounded-sm p-6"
    >
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-amber-800">
            <ShieldAlert className="h-3.5 w-3.5" /> Receipt not available
        </div>
        <p className="mt-3 text-sm text-amber-900">{message}</p>
        <p className="mt-2 font-mono text-xs text-amber-800 break-all">
            requested hash: {hash}
        </p>
    </div>
);

const ReceiptDocument = ({ receipt }) => {
    const verificationOk = receipt.verification === "SUCCESS";
    return (
        <article
            data-testid="public-receipt-card"
            className="border border-slate-300 bg-white rounded-sm receipt-paper relative overflow-hidden stamp-in"
        >
            <div className="absolute -right-8 -top-8 opacity-[0.07] pointer-events-none">
                <Stamp className="h-52 w-52 text-slate-900" />
            </div>

            {/* Document header */}
            <div className="px-10 pt-10 pb-6 border-b border-slate-300">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                    KATLAS-signed governed action · public view
                </div>
                <h1
                    data-testid="public-action-label"
                    className="mt-3 font-heading text-3xl lg:text-4xl font-bold tracking-tight text-slate-900"
                >
                    {receipt.action_label}
                </h1>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                    <span
                        data-testid="public-verification"
                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] ${
                            verificationOk
                                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                                : "bg-amber-50 border-amber-200 text-amber-800"
                        }`}
                    >
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Verification: {receipt.verification || "—"}
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-700">
                        <FileText className="h-3.5 w-3.5" />
                        {receipt.prompt_route_category}
                    </span>
                </div>
            </div>

            {/* Three questions */}
            <div className="px-10 py-8 grid grid-cols-1 md:grid-cols-3 gap-6 border-b border-slate-300">
                <QuestionBlock
                    n="1"
                    question="What governed action happened?"
                    testId="public-q1"
                >
                    <p className="text-sm text-slate-800 leading-relaxed">
                        <span className="font-semibold">{receipt.action_label}.</span>{" "}
                        Model route chosen:{" "}
                        <span className="font-mono text-xs">
                            {receipt.model_route_chosen}
                        </span>
                        .
                    </p>
                </QuestionBlock>
                <QuestionBlock
                    n="2"
                    question="Who had authority, and what outcome was recorded?"
                    testId="public-q2"
                >
                    <p className="text-sm text-slate-800 leading-relaxed">
                        {receipt.car_summary?.authority}
                    </p>
                </QuestionBlock>
                <QuestionBlock
                    n="3"
                    question="What does the KATLAS receipt prove?"
                    testId="public-q3"
                >
                    <p className="text-sm text-slate-800 leading-relaxed">
                        {receipt.car_summary?.receipts}{" "}
                        <span className="text-slate-500">
                            Private material stays at source.
                        </span>
                    </p>
                </QuestionBlock>
            </div>

            {/* Allow-listed fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 px-10 py-8">
                <Row icon={Hash} label="Receipt hash" value={receipt.receipt_hash} testId="public-row-hash" />
                <Row icon={Clock} label="KATLAS node timestamp" value={receipt.node_timestamp_utc} testId="public-row-node-ts" />
                <Row icon={KeyRound} label="Attestation reference" value={receipt.attestation_reference} testId="public-row-attestation" />
                <Row icon={KeyRound} label="Signer issuer" value={receipt.signer?.issuer} testId="public-row-issuer" />
                <Row icon={KeyRound} label="Signer key id" value={receipt.signer?.key_id} testId="public-row-keyid" />
                <Row icon={Fingerprint} label="Signer fingerprint" value={receipt.signer?.fingerprint} testId="public-row-fpr" />
                <Row icon={FileText} label="Prompt route category" value={receipt.prompt_route_category} mono={false} testId="public-row-category" />
                <Row icon={FileText} label="Model route chosen" value={receipt.model_route_chosen} mono={false} testId="public-row-model" />
                <Row
                    icon={ShieldCheck}
                    label={`Shared evidence refs · ${receipt.shared_evidence_refs?.length ?? 0}`}
                    value={(receipt.shared_evidence_refs || []).join("\n")}
                    testId="public-row-shared"
                />
                <Row
                    icon={ShieldAlert}
                    label="Withheld evidence count"
                    value={String(receipt.withheld_evidence_count ?? "")}
                    testId="public-row-withheld-count"
                />
            </div>

            {/* Cost rationale */}
            <div className="px-10 pb-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2">
                    High-level cost rationale
                </div>
                <p
                    data-testid="public-cost-rationale"
                    className="text-sm text-slate-700 leading-relaxed max-w-3xl"
                >
                    {receipt.cost_rationale_summary}
                </p>
            </div>

            {/* CAR */}
            <div
                data-testid="public-car-block"
                className="mx-10 mb-6 border border-dashed border-slate-300 rounded-sm p-6 bg-slate-50/50 grid grid-cols-1 md:grid-cols-3 gap-6"
            >
                <Car title="Custody" body={receipt.car_summary?.custody} />
                <Car title="Authority" body={receipt.car_summary?.authority} />
                <Car title="Receipts" body={receipt.car_summary?.receipts} />
            </div>

            <p
                data-testid="public-data-boundary"
                className="px-10 pb-10 text-xs text-slate-500 italic max-w-3xl"
            >
                {receipt.data_boundary_statement}
            </p>
        </article>
    );
};

const QuestionBlock = ({ n, question, children, testId }) => (
    <div data-testid={testId}>
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
            <HelpCircle className="h-3.5 w-3.5" /> Question {n}
        </div>
        <div className="mt-2 font-heading text-base font-semibold text-slate-900 leading-snug">
            {question}
        </div>
        <div className="mt-3">{children}</div>
    </div>
);

const Row = ({ icon: Icon, label, value, mono = true, testId }) => (
    <div
        data-testid={testId}
        className="flex items-start gap-3 py-3 border-b border-dashed border-slate-300/80 last:border-b-0"
    >
        <Icon className="h-4 w-4 mt-0.5 text-slate-500 shrink-0" />
        <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                {label}
            </div>
            <div
                className={`mt-1 ${mono ? "font-mono text-xs" : "text-sm"} text-slate-900 break-all whitespace-pre-line`}
            >
                {value || <span className="text-slate-400">—</span>}
            </div>
        </div>
    </div>
);

const Car = ({ title, body }) => (
    <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
            {title}
        </div>
        <p className="mt-2 text-sm text-slate-800 leading-relaxed">{body}</p>
    </div>
);

export default PublicReceiptPage;
